import { Link } from "react-router-dom";
import { ArrowUpRight, Copy, Eye, ScrollText } from "lucide-react";
import type { Wallet, WalletStatus, WalletType } from "@/types";
import { Badge } from "@/components/ui/badge";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { RowActionsMenu } from "@/components/ui/row-actions-menu";
import { toast } from "@/components/ui/toast";
import { SigningTopologyChip } from "@/components/data-display/signing-topology-chip";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { ValueCell } from "@/features/valuation/value-cell";
import { UNKNOWN_VALUE_LABEL } from "@/features/valuation/valuation";
import { WalletBalanceReadNotice, walletBalanceReadState } from "@/features/wallets/wallet-balance-read-notice";

/** Copies a value and says so; a refused clipboard write is still honest. */
async function copyText(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast({ variant: "success", title: `${label} copied` });
  } catch {
    toast({ variant: "error", title: "Couldn't copy — select the text and copy it manually" });
  }
}

const TYPE_META: Record<WalletType, { label: string; bg: string; text: string }> = {
  hot: { label: "HOT", bg: "var(--ads-danger-tint)", text: "var(--ads-danger)" },
  cold: { label: "CLD", bg: "var(--ads-info-soft)", text: "var(--ads-info)" },
  warm: { label: "WRM", bg: "var(--ads-warning-soft)", text: "var(--ads-warning)" },
};

const STATUS_META: Record<WalletStatus, { label: string; variant: "success" | "danger" | "pending" | "neutral" }> = {
  active: { label: "Active", variant: "success" },
  frozen: { label: "Frozen", variant: "danger" },
  pending: { label: "Pending", variant: "pending" },
  sandbox: { label: "Testnet", variant: "neutral" },
};

/**
 * ALL-5638: distinguish "the chain read failed" from "this wallet has not been
 * valued yet" — they render the same dash, but only the first is a support
 * matter, so the cell carries the gateway's status.
 */
function balanceCellStatus(wallet: Wallet): "UNAVAILABLE" | "UNVALUED" | "VALUED" {
  if (wallet.balanceStatus === "unavailable") return "UNAVAILABLE";
  return wallet.balanceUsd === null ? "UNVALUED" : "VALUED";
}

export function WalletRow({ wallet, href }: { wallet: Wallet; href?: string }) {
  const type = TYPE_META[wallet.type];
  const status = STATUS_META[wallet.status];
  // ALL-7239: one descriptor for both read-truth decisions. ValueCell renders a
  // supplied number even under an error status, so an explicit non-authoritative
  // read must pass null rather than rely on the status alone.
  const readState = walletBalanceReadState(wallet.balanceStatus);

  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-bg">
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-2">
          {href ? (
            <Link to={href} className="text-sm font-semibold text-ink transition-colors hover:text-primary">
              {wallet.name}
            </Link>
          ) : (
            <div className="text-sm font-semibold text-ink">{wallet.name}</div>
          )}
          <SigningTopologyChip signingTopology={wallet.signingTopology} />
        </div>
        {/* ALL-5795: the address under the name, shortened, with full value and copy. */}
        {wallet.address && (
          <div className="mt-0.5 text-xs text-muted" title={wallet.storedAlias ? `Stored name: ${wallet.storedAlias}` : undefined}>
            <IdentifierValue value={wallet.address} className="justify-start" />
          </div>
        )}
      </td>
      <td className="px-4 py-3.5 text-sm text-text">{wallet.chain}</td>
      <td className="px-4 py-3.5 text-sm text-text">
        {/* ALL-5638: the gateway sends the row's provider route; a row without
            one shows the dash rather than a guessed vendor name. */}
        {wallet.provider ?? UNKNOWN_VALUE_LABEL}
      </td>
      <td className="px-4 py-3.5">
        <span
          className="inline-flex items-center px-2 py-[3px] rounded-[4px] text-[11px] font-bold uppercase tracking-[0.04em]"
          style={{ background: type.bg, color: type.text }}
        >
          {type.label}
        </span>
      </td>
      <td className="px-4 py-3.5">
        <ValueCell
          value={readState ? null : wallet.balanceUsd}
          status={balanceCellStatus(wallet)}
          compact
        />
        {/* ALL-7239: the explicit read states (unavailable / unsupported /
            not_read) own their label, the frozen failed-read title and the
            copyable support reference. ok and legacy rows render nothing. */}
        {readState && (
          <WalletBalanceReadNotice
            status={wallet.balanceStatus}
            supportReference={wallet.balanceSupportReference}
          />
        )}
      </td>
      <td className="px-4 py-3.5">
        {readState ? (
          /* The read state is about the read, not the price, so it takes
             priority over the stale ago stamp and the "Not priced" marker. */
          <span className="text-sm text-muted">{readState.sourceLabel}</span>
        ) : wallet.lastSyncAgo ? (
          <span className="inline-flex items-center gap-1 text-sm text-muted">
            <span
              className="size-1.5 rounded-full shrink-0"
              style={{ background: wallet.status === "frozen" ? "var(--ads-warning)" : "var(--ads-success)" }}
            />
            {wallet.lastSyncAgo} ago
          </span>
        ) : wallet.balanceUsd === null ? (
          /* OPS-02/D15: a wallet with no balance and no chain read yet is
             unvalued, and a bare em dash reads as a missing/broken cell. Name
             the state instead, via the shared marker. A wallet that *has* a
             value but no freshness stamp keeps the plain dash — its absence is
             about the read, not the price. */
          <UnavailableValue label="Not priced" />
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3.5">
        <Badge variant={status.variant} dot={false}>{status.label}</Badge>
      </td>
      <td className="px-4 py-3.5">
        <RowActionsMenu
          rowLabel={wallet.name}
          items={[
            // Only items whose route exists in this app's router (App.tsx) are
            // offered. The design's "On-chain activity" (`/stablecoins/activity`)
            // has no route here, so it is deliberately absent rather than a
            // dead link.
            ...(href ? [{ label: "View wallet", href, icon: <Eye /> }] : []),
            { label: "New transfer", href: `/transfers/new?source=${encodeURIComponent(wallet.id)}`, icon: <ArrowUpRight /> },
            {
              label: "Copy wallet ID",
              icon: <Copy />,
              onSelect: () => {
                void copyText(wallet.id, "Wallet ID");
              },
            },
            {
              label: "Reconciliation",
              href: "/reconciliation",
              icon: <ScrollText />,
              separatorBefore: true,
            },
          ]}
        />
      </td>
    </tr>
  );
}
